import React, { useState } from 'react';
import { REGION_LABELS, REGION_STATUS_COLORS } from '../lib/fmsBodyRegions';
import bodyMale from '../assets/body-male.jpg';
import bodyFemale from '../assets/body-female.jpg';

// Marker positions as percentages of the photo's width/height (top-left
// origin), measured against a percentage grid overlaid on each reference
// photo - the two photos aren't identically proportioned (the female photo
// reads slightly shorter through the legs), so each gender gets its own
// measured set rather than sharing one.
//
// The client faces the camera, so their actual right side appears on the
// image's LEFT half (mirror convention, same as looking at someone face to
// face) - "_right"/"_left" below refer to the CLIENT's side, not the image
// side, to match REGION_LABELS ("Bark (L)"/"Bark (P)").
const REGION_POSITIONS_BY_GENDER = {
  male: {
    shoulder_right: { x: 30, y: 20 },
    shoulder_left: { x: 70, y: 20 },
    trunk: { x: 50, y: 30 },
    hip_right: { x: 39, y: 50 },
    hip_left: { x: 61, y: 50 },
    knee_right: { x: 42, y: 73 },
    knee_left: { x: 58, y: 73 },
    ankle_right: { x: 37, y: 87 },
    ankle_left: { x: 63, y: 87 },
  },
  female: {
    shoulder_right: { x: 34, y: 19 },
    shoulder_left: { x: 66, y: 19 },
    trunk: { x: 50, y: 31 },
    hip_right: { x: 38, y: 49 },
    hip_left: { x: 62, y: 49 },
    knee_right: { x: 42, y: 69 },
    knee_left: { x: 58, y: 69 },
    ankle_right: { x: 38, y: 85 },
    ankle_left: { x: 62, y: 85 },
  },
};

const REGION_SIZE = {
  trunk: 34, // bigger hit target - covers chest+abdomen
};

const Marker = ({ region, pos, status, isSelected, onClick }) => {
  const colors = REGION_STATUS_COLORS[status];
  const size = REGION_SIZE[region] || 22;
  return (
    <button
      onClick={() => onClick(region)}
      className="absolute rounded-full transition-all cursor-pointer"
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: size,
        height: size,
        transform: 'translate(-50%, -50%)',
        backgroundColor: colors.fill,
        opacity: status === 'unknown' ? 0.45 : 0.8,
        border: `2px solid ${isSelected ? '#FFFFFF' : colors.stroke}`,
        boxShadow: isSelected ? '0 0 0 3px rgba(255,255,255,0.3)' : 'none',
      }}
      aria-label={REGION_LABELS[region]}
      data-testid={`body-region-${region}`}
    />
  );
};

export const FMSBodyMap = ({ regionStatuses, gender }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const selected = selectedRegion ? regionStatuses[selectedRegion] : null;
  const resolvedGender = gender === 'female' ? 'female' : 'male';
  const photo = resolvedGender === 'female' ? bodyFemale : bodyMale;
  const positions = REGION_POSITIONS_BY_GENDER[resolvedGender];

  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start" data-testid="fms-body-map">
      <div className="relative w-full max-w-[220px] mx-auto sm:mx-0 shrink-0 rounded-2xl overflow-hidden bg-black">
        <img src={photo} alt="" className="w-full h-auto block select-none" draggable={false} />
        {Object.keys(positions).map(region => (
          <Marker
            key={region}
            region={region}
            pos={positions[region]}
            status={regionStatuses[region]?.status || 'unknown'}
            isSelected={selectedRegion === region}
            onClick={setSelectedRegion}
          />
        ))}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(REGION_STATUS_COLORS).map(([key, c]) => (
            <div key={key} className="flex items-center gap-2 text-xs text-[#A1A1A1]">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.fill }} />
              {c.label}
            </div>
          ))}
        </div>

        {!selected ? (
          <p className="text-sm text-[#A1A1A1] body-text">
            Kliknij element ciała na diagramie, aby zobaczyć powiązane testy i notatki.
          </p>
        ) : (
          <div className="card-surface rounded-2xl p-5" data-testid="body-region-detail">
            <div className="flex items-center justify-between mb-3">
              <h4 className="heading-text text-lg text-white">{REGION_LABELS[selectedRegion]}</h4>
              <span className={`text-xs font-medium px-3 py-1 rounded-full ${REGION_STATUS_COLORS[selected.status].text}`}>
                {REGION_STATUS_COLORS[selected.status].label}
              </span>
            </div>
            {selected.tests.length === 0 ? (
              <p className="text-sm text-[#A1A1A1]">Ten obszar nie był oceniany w tej ocenie.</p>
            ) : (
              <div className="space-y-3">
                {selected.tests.map((t, i) => (
                  <div key={i} className="pb-3 border-b border-[#2A2A2A] last:border-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm font-medium">{t.test_name}</span>
                      <span className="text-[#C9A84C] font-bold">{t.score}</span>
                    </div>
                    <div className="flex gap-2 mt-1">
                      {t.pain_flag && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">BÓL</span>}
                      {t.asymmetry_flag && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">ASYMETRIA L/P</span>}
                    </div>
                    {t.notes && <p className="text-xs text-[#A1A1A1] mt-1">{t.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
